<template>
    <div class="ms-main-app-container">
        <TheSideMenu
            :mode="layoutMode"
            :expanded="sideNavExpanded"
            :pinned="sideNavPinned"
            @navigate="closeOverlayMenu"
            @toggle-menu="toggleMenuv2()"
            @mouseenter="onMouseenter"
            @mousemove="onMousemove"
            @mouseleave="onMouseleave"
        />

        <div v-if="layoutMode === 'tablet' && overlayOpen" class="side-nav-backdrop" @click="overlayOpen = false" />

        <section class="ms-main-content-container">
            <HeaderBar
                :menu-open="layoutMode !== 'desktop' && overlayOpen"
                :show-menu-toggle="layoutMode !== 'desktop'"
                @toggle-menu="toggleMenuv2()"
            />
            <notification-bar
                v-if="showNotificationBar"
                :subscription-status="$auth.session.home.subscriptionStatus"
            ></notification-bar>

            <!-- <perfect-scrollbar
                id="scroll"
                v-bind:class="{ 'red-background': hasBackground }"
                :options="{ wheelPropagation: true }"
            > -->
            <div id="scroll" class="main-wrapper" :class="{ 'red-background': hasBackground }">
                <main class="ms-main-content" :class="{ 'contained-content': contained }">
                    <promo-banner />
                    <div class="ms-main-content-body">
                        <slot />
                    </div>
                    <div
                        v-if="
                            !$auth.role?.includes('SuperAdmin') &&
                            $auth.session &&
                            $auth.session.home &&
                            ($auth.session.home.subscriptionStatus === 5 ||
                                $auth.session.home.subscriptionStatus === 6 ||
                                $auth.session.home.subscriptionStatus === 7)
                        "
                        class="ms-notification-bar-spacer"
                    />
                    <div class="imhere" style="width: 100%; height: 2rem" />
                </main>
            </div>
            <!-- </perfect-scrollbar> -->
        </section>
    </div>
</template>

<script>
import TheSideMenu from '@/components/layout/TheSideMenu.vue';
import HeaderBar from '@/components/layout/HeaderBar.vue';
import PromoBanner from '@/components/layout/PromoBanner.vue';
import NotificationBar from '@/components/layout/NotificationBar.vue';
import {
    getSideNavPinnedPreference,
    SIDE_NAV_PINNED_STORAGE_KEY,
} from '../../utilities/general';
import { useGlobalState } from '@/stores/globalState';
import { useSideNavHover } from '@/composables/useSideNavHover';
import { throttle } from 'lodash';

export default {
    name: 'AppLayout',
    components: {
        TheSideMenu,
        HeaderBar,
        PromoBanner,
        NotificationBar,
    },
    props: {
        hasBackground: {
            type: Boolean,
            default: true,
        },
        contained: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const sideNavHover = useSideNavHover();

        return {
            globalState: useGlobalState(),
            ...sideNavHover,
        };
    },
    data: function () {
        return {
            mouseTimeout: null,
            autoExpanded: false,
            isSideNavPointerInside: false,
            mousePos: { x: 0, y: 0 },
            useHoverSideNav: true,
            overlayOpen: false,

            canUseHoverNav: window.matchMedia('(hover: hover) and (pointer: fine)').matches,
            hoverNavMediaQuery: null,
        };
    },
    computed: {
        layoutMode() {
            return this.getLayoutModeForWidth(this.$vuetify.display.width);
        },
        sideNavExpanded() {
            if (this.layoutMode !== 'desktop') {
                return this.overlayOpen;
            }

            return this.globalState.menuShowing || this.isHoverExpanded;
        },
        sideNavPinned() {
            return this.layoutMode === 'desktop' && this.globalState.menuShowing;
        },
        showNotificationBar() {
            const isSuperAdmin = this.$auth.role?.includes('SuperAdmin');

            const hasActiveSession = this.$auth.session && this.$auth.session.home;

            const triggeringStatuses = [1, 5, 6, 7];

            const hasTriggeringStatus =
                hasActiveSession && triggeringStatuses.includes(this.$auth.session.home.subscriptionStatus);

            return !isSuperAdmin && hasActiveSession && hasTriggeringStatus;
        },
    },
    watch: {
        layoutMode(nextMode, previousMode) {
            if (nextMode === previousMode) {
                return;
            }

            this.clearSideNavHover();
            this.overlayOpen = false;

            if (nextMode === 'desktop') {
                this.applyStoredSideNavPreference();
            }
        },
    },

    created() {
        this.applyStoredSideNavPreference();
    },

    mounted() {
        document.documentElement.style.overflow = 'hidden';

        this.hoverNavMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.canUseHoverNav = this.hoverNavMediaQuery.matches;
        this.hoverNavMediaQuery.addEventListener('change', this.onHoverNavCapabilityChange);

        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            if (this.globalState.menuShowing) {
                this.globalState.menuShowing = false;
            }
        }

        window.addEventListener('storage', this.onStorageChange);

        this.interval = setInterval(this.checkIntercomBanner, 2000);

        // clears interval for banner check after 10s
        setTimeout(() => {
            clearInterval(this.interval);
        }, 10000);
    },
    beforeUnmount() {
        this.hoverNavMediaQuery?.removeEventListener('change', this.onHoverNavCapabilityChange);
        window.removeEventListener('storage', this.onStorageChange);

        if (!this.isSideNavPointerInside) {
            this.clearSideNavHover();
        }
    },
    methods: {
        applyStoredSideNavPreference() {
            const pinnedPreference = getSideNavPinnedPreference();

            if (typeof pinnedPreference === 'boolean') {
                this.globalState.menuShowing = pinnedPreference;
                return;
            }

            this.globalState.menuShowing = false;
        },
        getLayoutModeForWidth(width) {
            if (width <= 768) {
                return 'mobile';
            }

            if (!this.canUseHoverNav) {
                return 'tablet';
            }

            return 'desktop';
        },
        onHoverNavCapabilityChange(event) {
            const previousLayoutMode = this.layoutMode;
            this.canUseHoverNav = event.matches;

            if (previousLayoutMode !== this.layoutMode) {
                this.clearSideNavHover();
                this.overlayOpen = false;
            }
        },
        toggleMenuv2(saveChange = true) {
            this.clearSideNavHover();

            if (this.layoutMode !== 'desktop') {
                this.overlayOpen = !this.overlayOpen;
                return;
            }

            this.globalState.toggleSideNav(saveChange);
        },
        closeOverlayMenu() {
            if (this.layoutMode !== 'desktop') {
                this.overlayOpen = false;
            }
        },

        onStorageChange(event) {
            if (event.key !== SIDE_NAV_PINNED_STORAGE_KEY) {
                return;
            }

            if (this.layoutMode !== 'desktop') {
                return;
            }

            if (event.newValue === null) {
                this.globalState.menuShowing = false;
                return;
            }

            try {
                this.globalState.menuShowing = JSON.parse(event.newValue);
            } catch {
                this.globalState.menuShowing = false;
            }
        },

        openBillingPortal() {
            const funeralHomeId = this.$auth.session.home.id;
            this.$api
                .get(`/billing/funeralhome/portal/${funeralHomeId}`)
                .then(response => {
                    window.open(response.data, '_blank');
                })
                .catch(error => {});
        },
        checkIntercomBanner() {
            let bannerFrame = document.getElementsByName('intercom-banner-frame')[0];

            if (bannerFrame) {
                var iframeStyles = window.getComputedStyle(bannerFrame);

                let bannerStyle = iframeStyles.maxWidth == '800px' ? 'floating' : 'inline';
                let position = iframeStyles.top == '0px' || iframeStyles.top == '16px' ? 'top' : 'bottom';

                if (bannerStyle == 'inline') {
                    const closeBtn = bannerFrame.contentWindow.document.querySelectorAll('[role="button"]')[0];
                    const bannerHeight = bannerFrame.contentWindow.innerHeight;
                    if (closeBtn) {
                        // Revert page height if banner dismissed
                        closeBtn.addEventListener('click', function () {
                            document.documentElement.style.setProperty('--banner-spacer', '0px');
                        });
                    }
                    //adjusts app container height in event of intercom banner
                    document.documentElement.style.setProperty('--banner-spacer', bannerHeight + 'px');
                }

                clearInterval(this.interval);
            }
        },
        onMouseenter(evt) {
            this.isSideNavPointerInside = true;

            if (this.layoutMode !== 'desktop') {
                return;
            }

            if (this.useHoverSideNav) {
                this.expandSideNavOnHover();
                return;
            }

            if (this.$vuetify.display.width <= 768) {
                return;
            }
            if (!this.globalState.menuShowing) {
                const { x, y } = evt;
                this.mousePos = { x, y };
                this.mouseTimeout = setTimeout(() => {
                    this.autoExpanded = true;
                    this.toggleMenuv2(false);
                }, 3000);
            }
        },
        onMousemove: throttle(function (evt) {
            if (this.useHoverSideNav) {
                return;
            }

            if (!this.globalState.menuShowing) {
                const { x, y } = evt;
                // console.log('mousemove', {x, y});
                if (this.mouseTimeout) {
                    clearTimeout(this.mouseTimeout);
                }
                if (
                    Math.abs(Math.abs(x) - Math.abs(this.mousePos.x)) < 10 &&
                    Math.abs(Math.abs(y) - Math.abs(this.mousePos.y)) < 10
                ) {
                    this.mouseTimeout = setTimeout(() => {
                        this.autoExpanded = true;
                        this.toggleMenuv2(false);
                    }, 1200);
                }
                this.mousePos = { x, y };
            }
        }, 300),
        onMouseleave(evt) {
            this.isSideNavPointerInside = false;

            if (this.layoutMode !== 'desktop') {
                return;
            }

            if (this.useHoverSideNav) {
                this.collapseSideNavAfterHover();
                return;
            }

            if (this.$vuetify.display.width <= 768) {
                return;
            }
            this.mousePos = { x: 0, y: 0 };
            if (this.mouseTimeout) {
                clearTimeout(this.mouseTimeout);
            }
        },
    },
};
</script>

<style lang="scss">
:root {
    --banner-spacer: 0;
}

// .intercom-container.intercom-inline-frame {
//     /* CSS styles for inline banner */
//     border: 2px solid red !important;
// }
// .intercom-container.intercom-messenger-frame {
//     /* CSS styles for floating banner */
//     border: 2px solid purple !important;
// }

$main-header-height: 60px;
.contained-content {
    padding: 2rem;
}

.ms-main-app-container {
    // height: calc(100vh - var(--banner-spacer));
    min-height: 0;
    height: 100%;
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    overflow: hidden;
    position: relative;

    .ms-notification-bar-spacer {
        height: 4rem;
    }

    .side-nav-backdrop {
        background: transparent;
        inset: 0;
        position: fixed;
        z-index: 1190;
    }

    .ms-main-content-container {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        flex: 1;
        position: relative;
        min-width: 0;
        min-height: 0;

        .main-wrapper {
            overflow: hidden;
            display: flex;
            flex: 1;
            min-height: 0;
        }

        .ms-main-header {
            height: $main-header-height;
            box-sizing: border-box;
            background: #f1f4f7;
            box-shadow: 0 0.1rem 0.25rem 0 rgba(31, 45, 65, 0.1);
            flex: 0 0 $main-header-height;
            display: flex;
            align-items: center;
            padding: 0 2rem;

            .menu-toggle-button {
                cursor: pointer;
                @include mobile() {
                    position: relative;
                    z-index: 10;
                }
            }
        }

        .ms-notification-bar {
            height: 80px;
            // position: fixed;
            position: sticky;

            z-index: 1000;
            background: rgb(246, 244, 198);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 0 2rem;
            top: $main-header-height;

            &-content-left {
                display: flex;
                align-items: center;
                flex-grow: 1;
                justify-content: center;
            }

            p {
                max-width: 100%;
                margin: 0;
            }
        }

        .ms-main-content {
            display: flex;
            flex-direction: column;
            flex: 1;
            max-width: 100%;
            min-width: 0;
            min-height: 0;
            overflow: auto;
        }

        .ms-main-content-body {
            display: flex;
            flex: 1 0 auto;
            flex-direction: column;
            min-height: 0;
            width: 100%;
        }

        .ms-main-content-body > :only-child {
            flex: 1 0 auto;
        }

        .red-background {
            background: url('/swoop.svg') no-repeat fixed 0 -16rem;
        }
    }
}

@media only screen and (max-width: 768px) {
    .ms-notification-bar {
        height: 200px !important;
    }

    .ms-notification-bar-spacer {
        height: 220px !important;
        width: 100%;
    }
}
</style>
